import type { SnapshotConversationMessage } from "./types";

interface SendState {
  sentMessageId: string | null;
  /** False until saved messages change after a send. Until then they cannot contain the sent prompt. */
  savedCaughtUp: boolean;
}

const contentKey = (message: SnapshotConversationMessage) => `${message.role}\0${message.content}`;

/** Saved messages already hold everything before the latest sent prompt, so only its turn needs keeping. */
export const keepLatestTurn = (
  local: SnapshotConversationMessage[],
  sentMessageId: string | null,
): SnapshotConversationMessage[] => {
  const sentIndex = local.findIndex(m => m.id === sentMessageId);
  return sentIndex > 0 ? local.slice(sentIndex) : local;
};

export const mergeConversationMessages = (
  saved: SnapshotConversationMessage[],
  local: SnapshotConversationMessage[],
  { sentMessageId, savedCaughtUp }: SendState,
): SnapshotConversationMessage[] => {
  const savedIds = new Set(saved.map(m => m.id));
  const savedContentCounts = new Map<string, number>();
  for (const m of saved) {
    savedContentCounts.set(contentKey(m), (savedContentCounts.get(contentKey(m)) ?? 0) + 1);
  }

  // Earlier local messages stay until saved messages catch up, so an answer does not vanish on send. Afterwards
  // they must go: a saved answer can differ from its local copy, and content deduplication would show both.
  const sentIndex = local.findIndex(m => m.id === sentMessageId);
  const candidates = savedCaughtUp ? (sentIndex < 0 ? [] : local.slice(sentIndex)) : local;

  const uniqueLocal = candidates.filter(m => {
    if (savedIds.has(m.id)) return false;
    if (!savedCaughtUp && m.id === sentMessageId) return true;
    const count = savedContentCounts.get(contentKey(m)) ?? 0;
    if (count > 0) {
      savedContentCounts.set(contentKey(m), count - 1);
      return false;
    }
    return true;
  });

  return [...saved, ...uniqueLocal].sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
};
